<script lang="ts">
	import { Badge } from '$lib/components/ui/badge';
	import { Button } from '$lib/components/ui/button';
	import * as Carousel from '$lib/components/ui/carousel';
	import * as m from '$lib/paraglide/messages.js';
	import { cn } from '$lib/utils.js';
	import {
		getListingDetailPath,
		getListingPresentation,
		type ListingBadgeGroup
	} from '$lib/listing-display';
	import { localizeHref } from '@/paraglide/runtime';
	import type { ListingsResponse } from '@/pocketbase/pocketbase-types';
	import {
		Collections,
		ListingsKindOptions,
		ListingsPricingTypeOptions
	} from '@/pocketbase/pocketbase-types';
	import { pb } from '@/utils/ApiClient';
	import Calendar from '@lucide/svelte/icons/calendar';
	import ChevronLeft from '@lucide/svelte/icons/chevron-left';
	import ChevronRight from '@lucide/svelte/icons/chevron-right';
	import Heart from '@lucide/svelte/icons/heart';
	import MapPin from '@lucide/svelte/icons/map-pin';
	import ShipWheel from '@lucide/svelte/icons/ship-wheel';
	import Sparkles from '@lucide/svelte/icons/sparkles';
	import Star from '@lucide/svelte/icons/star';
	import BadgeEuro from '@lucide/svelte/icons/badge-euro';
	import Users from '@lucide/svelte/icons/users';
	import { onMount } from 'svelte';
	import { toast } from 'svelte-sonner';
	import { resolve } from '$app/paths';

	interface Props {
		class?: string | undefined | null;
		aspectRatio?: 'portrait' | 'square';
		width: number;
		height: number;
		listing: ListingsResponse;
		minPriceFromRanges?: number | null;
		children?: import('svelte').Snippet;
		disableDrag?: boolean;
	}

	let {
		class: className = undefined,
		aspectRatio = 'square',
		width,
		height,
		listing,
		minPriceFromRanges = null,
		children,
		disableDrag = false,
		...restProps
	}: Props = $props();

	let isFavoritePressed = $state(false);
	let carouselApi = $state<any>();
	let selectedSlideIndex = $state(0);

	let favoriteId: string | null = $state(null);
	let averageRating = $state<number | null>(null);
	let reviewCount = $state<number>(0);

	// Función para verificar si el listing está en favoritos
	async function checkFavoriteStatus() {
		try {
			// Solo verificar si el usuario está autenticado
			if (!pb.authStore.isValid) {
				isFavoritePressed = false;
				favoriteId = null;
				return;
			}

			// Buscar si existe un favorito para este listing
			const existingFavourites = await pb.collection('favourites').getFullList({
				filter: `user = "${pb.authStore.record?.id}" && listing = "${listing.id}"`,
				requestKey: null
			});

			if (existingFavourites.length > 0) {
				isFavoritePressed = true;
				favoriteId = existingFavourites[0].id;
			} else {
				isFavoritePressed = false;
				favoriteId = null;
			}
		} catch (error) {
			console.error('Error al verificar estado de favorito:', error);
			isFavoritePressed = false;
			favoriteId = null;
		}
	}

	async function handleFavouriteClick() {
		try {
			// Verificar si el usuario está autenticado
			if (!pb.authStore.isValid) {
				console.error('Usuario no autenticado');
				toast.error('Debes iniciar sesión para agregar favoritos', {
					description: 'Por favor, inicia sesión o crea una cuenta para guardar tus favoritos.'
				});
				return;
			}

			if (isFavoritePressed && favoriteId) {
				// Si ya es favorito, eliminar
				await pb.collection('favourites').delete(favoriteId);
				isFavoritePressed = false;
				favoriteId = null;
				console.log('Favorito eliminado');
				toast.success('Favorito eliminado', {
					description: 'El anuncio se ha eliminado de tus favoritos.'
				});
			} else {
				// Si no es favorito, crear uno nuevo
				const newFavorite = await pb.collection('favourites').create({
					user: pb.authStore.record?.id,
					listing: listing.id
				});
				isFavoritePressed = true;
				favoriteId = newFavorite.id;
				console.log('Favorito agregado');
				toast.success('Favorito agregado', {
					description: 'El anuncio se ha agregado a tus favoritos.'
				});
			}
		} catch (error) {
			console.error('Error al manejar favorito:', error);
			toast.error('Error al manejar favorito', {
				description: 'Ha ocurrido un error. Por favor, intenta nuevamente.'
			});
		}
	}

	// Función para obtener y calcular el promedio de reseñas
	async function loadReviews() {
		if (!listing?.id) return;

		try {
			const reviews = await pb.collection(Collections.Reviews).getFullList({
				filter: `listing = "${listing.id}"`,
				requestKey: null
			});

			reviewCount = reviews.length;

			if (reviews.length > 0) {
				const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
				averageRating = totalRating / reviews.length;
			} else {
				averageRating = null;
			}
		} catch (error) {
			console.error('Error al cargar reseñas:', error);
			averageRating = null;
			reviewCount = 0;
		}
	}

	// Verificar el estado del favorito y cargar reseñas al montar el componente
	onMount(() => {
		checkFavoriteStatus();
		loadReviews();
	});

	$effect(() => {
		if (carouselApi) {
			selectedSlideIndex = carouselApi.selectedScrollSnap();
			const unsubscribe = carouselApi.on('select', () => {
				selectedSlideIndex = carouselApi.selectedScrollSnap();
			});
			return unsubscribe;
		}
	});

	// Función para formatear el precio
	function formatPrice(price: number): string {
		return new Intl.NumberFormat('es-ES', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 0
		}).format(price);
	}

	// Función segura para obtener las URLs de las imágenes
	function getImageUrls(): string[] {
		try {
			const expand = listing?.expand as Record<string, any> | undefined;

			// Primero, verificar si el listing tiene fotos propias
			if (listing?.photos && Array.isArray(listing.photos) && listing.photos.length > 0) {
				// Si el listing tiene fotos, usar esas
				return listing.photos.map((photo: string) =>
					pb.files.getURL(expand?.boat, photo, {
						thumb: '0x450'
					})
				);
			}

			// Si el listing no tiene fotos, usar las fotos del barco
			if (
				expand &&
				expand.boat &&
				Array.isArray(expand.boat.photos) &&
				expand.boat.photos.length > 0
			) {
				return expand.boat.photos.map((photo: string) =>
					pb.files.getURL(expand.boat, photo, {
						thumb: '0x450'
					})
				);
			}
		} catch (error) {
			console.error('Error al obtener las URLs de las imágenes:', error);
		}
		return ['/placeholder-boat.jpg']; // Imagen de respaldo
	}

	function badgeIconForGroup(group: ListingBadgeGroup) {
		switch (group) {
			case 'salida_compartida':
				return Sparkles;
			case 'patron_profesional':
				return BadgeEuro;
			case 'lista6_sin_patron':
			case 'sin_patron':
				return ShipWheel;
			default:
				return ShipWheel;
		}
	}

	// Función segura para obtener el nombre del puerto
	function getPortName() {
		const expand = listing?.expand as Record<string, any> | undefined;
		if (
			expand &&
			expand.boat &&
			expand.boat.expand &&
			expand.boat.expand.port &&
			expand.boat.expand.port.name
		) {
			return expand.boat.expand.port.name;
		}
		return 'Ubicación no disponible';
	}

	// Obtener todas las imágenes usando $derived
	const imageUrls = $derived(getImageUrls());
	const listingPresentation = $derived(
		listing
			? getListingPresentation(listing)
			: getListingPresentation({ kind: ListingsKindOptions.experience })
	);
	const BadgeIcon = $derived(badgeIconForGroup(listingPresentation.badgeGroup));
	const portName = $derived(getPortName());
	const displayPrice = $derived(minPriceFromRanges ?? null);
	const localizedListingPathForResolve = $derived(
		listing
			? (localizeHref(getListingDetailPath(listing)) as '/barcos')
			: (localizeHref('/barcos') as '/barcos')
	);
</script>

{#if children}{@render children()}{/if}
<div
	class={cn(
		'group relative overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl',
		className
	)}
	{...restProps}
>
	<div class="relative overflow-hidden">
		<!-- Botón de favorito -->
		<div class="absolute top-2 right-2 z-10">
			<button
				class="rounded-full bg-white/80 p-1.5 text-gray-500 shadow-xs backdrop-blur-xs transition-all duration-150 hover:bg-white hover:text-red-500 {isFavoritePressed
					? 'text-red-500'
					: ''}"
				onclick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleFavouriteClick();
				}}
			>
				<Heart class="h-5 w-5" fill={isFavoritePressed ? 'currentColor' : 'none'} />
			</button>
		</div>

		<!-- Carrusel de imágenes -->
		<a href={resolve(localizedListingPathForResolve)} class="block">
			<Carousel.Root
				opts={{
					loop: true,
					dragFree: !disableDrag
				}}
				class="w-full"
				setApi={(api) => (carouselApi = api)}
			>
				<Carousel.Content>
					{#each imageUrls as imageUrl, i (i)}
						<Carousel.Item>
							<div class="relative">
								<img
									class={cn(
										'h-auto w-full object-cover transition-all duration-500',
										aspectRatio === 'portrait' ? 'aspect-3/4' : 'aspect-square'
									)}
									src={imageUrl}
									alt={`${listing?.title || 'Imagen de barco'} ${i + 1}`}
									width={width}
									height={height}
									loading={i === 0 ? 'eager' : 'lazy'}
								/>
							</div>
						</Carousel.Item>
					{/each}
				</Carousel.Content>

				<!-- Controles de navegación del carrusel - visibles solo en pantallas más grandes -->
				{#if imageUrls.length > 1}
					<div class="absolute inset-0 hidden items-center justify-between px-2 md:flex">
						<button
							class="flex h-8 w-8 items-center justify-center rounded-full bg-white/70 text-gray-800 backdrop-blur-xs transition-all hover:bg-white"
							onclick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								carouselApi?.scrollPrev();
							}}
						>
							<ChevronLeft class="h-5 w-5" />
						</button>
						<button
							class="flex h-8 w-8 items-center justify-center rounded-full bg-white/70 text-gray-800 backdrop-blur-xs transition-all hover:bg-white"
							onclick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								carouselApi?.scrollNext();
							}}
						>
							<ChevronRight class="h-5 w-5" />
						</button>
					</div>
				{/if}

				<!-- Indicadores de posición como puntos -->
				{#if imageUrls.length > 1}
					<div class="absolute right-0 bottom-2 left-0 flex justify-center gap-2">
						{#each Array.from(imageUrls.keys()) as i (i)}
							<button
								class="h-2 w-2 rounded-full transition-all {selectedSlideIndex === i
									? 'scale-125 bg-white'
									: 'bg-white/50'}"
								onclick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									carouselApi?.scrollTo(i);
								}}
								aria-label={`Ver imagen ${i + 1} de ${imageUrls.length}`}
							></button>
						{/each}
					</div>
				{/if}
			</Carousel.Root>
		</a>

		<!-- Etiqueta de tipo de capitán requerido -->
		{#if listing?.kind}
			<Badge
				class={`absolute top-2 left-2 z-10 border py-1 pr-2 pl-1 ${listingPresentation.badgeClass} backdrop-blur-xs`}
			>
				<BadgeIcon class="mr-1 h-3.5 w-3.5 shrink-0" />
				{listingPresentation.shortLabel}
			</Badge>
		{/if}

		<!-- Precio - Ahora con un diseño más integrado y menos parecido a un botón -->
		<div
			class="absolute right-0 bottom-0 z-10 bg-linear-to-t from-black/70 to-transparent p-2 text-right"
		>
			{#if displayPrice !== null}
				<span class="text-lg font-bold text-white">{formatPrice(displayPrice)}</span>
				<span class="text-xs text-white">
					{#if listing?.pricingType === ListingsPricingTypeOptions.per_seat}
						/persona
					{:else}
						{m.dirty_stock_jan_care()}
					{/if}
				</span>
			{/if}
		</div>
	</div>

	<div class="p-4">
		<!-- Título y capacidad -->
		<div class="mb-2 flex items-start justify-between">
			<h3 class="min-h-10 md:min-h-12">
				<a
					href={resolve(localizedListingPathForResolve)}
					class="focus-visible:ring-ring line-clamp-3 text-sm leading-tight font-semibold text-inherit hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:line-clamp-2 md:text-lg"
				>
					{listing?.title}
				</a>
			</h3>
		</div>

		<!-- Información adicional -->
		<div class="space-y-2 text-sm">
			<!-- Ubicación -->
			<div class="flex items-center text-gray-600">
				<MapPin class="mr-1.5 h-4 w-4 shrink-0" />
				<span class="truncate">
					{portName}
				</span>
			</div>

			<!-- Capacidad y Reseñas en la misma línea -->
			<div class="flex items-center gap-3 text-gray-600">
				<div class="flex min-w-0 flex-1 items-center">
					<Users class="mr-1.5 h-4 w-4 shrink-0" />
					<span class="truncate">
						{#if listing?.spots}
							{listing?.spots} {listing?.spots === 1 ? 'persona' : m.each_legal_hound_arrive()}
						{:else}
							Capacidad no disponible
						{/if}
					</span>
				</div>
				{#if averageRating !== null && reviewCount > 0}
					<div class="flex shrink-0 items-center gap-1">
						<div class="flex items-center gap-0.5">
							{#each [0, 1, 2, 3, 4] as i (i)}
								<Star
									class={cn(
										'h-3.5 w-3.5 shrink-0',
										i < Math.round(averageRating)
											? 'fill-amber-400 text-amber-400'
											: 'text-gray-300'
									)}
								/>
							{/each}
						</div>
						<span class="text-xs font-medium whitespace-nowrap text-gray-700">
							{averageRating.toFixed(1)}
						</span>
						<span class="text-xs whitespace-nowrap text-gray-500">
							({reviewCount})
						</span>
					</div>
				{/if}
			</div>

			<!-- Botón de Reservar ahora (reemplaza la sección de disponibilidad) -->
			<div class="mt-3 pt-1">
				<a href={resolve(localizedListingPathForResolve)} class="block">
					<Button class="w-full bg-blue-700 font-medium hover:bg-blue-800">
						<Calendar class="mr-1.5 hidden h-4 w-4 sm:block" />
						{listing?.kind === ListingsKindOptions.experience
							? 'Ver salida'
							: m.gross_alive_moth_sway()}
						<span class="hidden md:inline"
							>{listing?.kind === ListingsKindOptions.experience
								? ''
								: m.icy_blue_liger_prosper()}</span
						>
					</Button>
				</a>
			</div>
		</div>
	</div>
</div>
